import * as React from 'react';
import type { FieldPath, FieldValues, UseControllerProps, UseFormUnregister } from 'react-hook-form';

import type { ColorPalette } from '@acceligentllc/shared/enums/color';

import type { Position, Alignment } from 'af-components/Controls/ColorPicker/ColorsGrid';
import type { ValueRenderMethodParams } from 'af-components/RHFFieldWrappers/RHFValueFieldWrapper';
import RHFValueFieldWrapper from 'af-components/RHFFieldWrappers/RHFValueFieldWrapper';

import ColorPickerCell from '../ColorPickerCell';

type Props<
	TFieldValues extends FieldValues,
	TName extends FieldPath<TFieldValues>,
	TTransformedValues
> = UseControllerProps<TFieldValues, TName, TTransformedValues> & {
	rhfId: string | number;
	fieldsLength: number;
	unregister: UseFormUnregister<TFieldValues>;
	onChange?: (name: string) => void;
	onBlur: () => void;
	fixed?: boolean;

	position: Position;
	align: Alignment;
	showAdditionalOptions?: boolean;
	disabled?: boolean;
};

export function ColorPickerField<
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
	TTransformedValues = TFieldValues
>(props: Props<TFieldValues, TName, TTransformedValues>) {
	const {
		name,
		rhfId,
		position,
		align,
		showAdditionalOptions = false,
		disabled = false,
		fixed,
	} = props;

	const handleChange = React.useCallback((
		onFieldChange: (color: ColorPalette) => void,
		onFieldBlur: () => void) => (
			color: ColorPalette
		) => {
			onFieldChange(color);
			onFieldBlur();
		}, []);

	const render = React.useCallback((
		params: ValueRenderMethodParams<TFieldValues, TName, ColorPalette>
	) => {
		const { field, error, onFieldChange, onFieldBlur } = params;

		const value = (field.value ?? null) as Nullable<ColorPalette>;

		return (
			<ColorPickerCell
				align={align}
				disabled={disabled}
				error={error ?? undefined}
				fixed={fixed}
				onChange={handleChange(onFieldChange, onFieldBlur)}
				position={position}
				showAdditionalOptions={showAdditionalOptions}
				value={value}
			/>
		);
	}, [align, disabled, fixed, handleChange, position, showAdditionalOptions]);

	return (
		<RHFValueFieldWrapper<TFieldValues, TName, TTransformedValues, ColorPalette>
			{...props}
			key={`${name}_${rhfId}`}
			render={render}
		/>
	);
}
